<template>
    <section class="ec-grafico" :aria-label="titulo">
        <header class="ec-grafico__cabecalho">
            <div class="ec-grafico__titulos">
                <span class="ec-grafico__titulo">{{ titulo }}</span>
                <span class="ec-grafico__total">{{ formatar(total) }}</span>
                <span class="ec-grafico__subtitulo">{{ subtitulo }}</span>
            </div>
            <div class="ec-grafico__acoes">
                <button type="button" class="ec-grafico__alternar" :class="{ ativo: exibirTabela }" @click="exibirTabela=!exibirTabela" :aria-pressed="exibirTabela ? 'true' : 'false'">
                    <i :class="exibirTabela ? 'fa-solid fa-chart-column' : 'fa-solid fa-table'"></i>
                    {{ exibirTabela ? 'Gráfico' : 'Tabela' }}
                </button>
                <a v-if="textoLink" href="#" class="ec-grafico__link" @click.prevent="$emit('abrir')">
                    {{ textoLink }} <i class="fa-solid fa-arrow-right"></i>
                </a>
            </div>
        </header>

        <div v-if="!itens.length" class="ec-grafico__vazio">
            <i class="fa-regular fa-chart-bar"></i>
            Nenhum registro para exibir.
        </div>

        <div v-else-if="!exibirTabela" class="ec-grafico__area">
            <Bar :options="opcoes" :data="dados" :plugins="plugins" />
        </div>

        <!-- mesma altura do gráfico: alternar não faz o cartão "pular"; cabeçalho fixo na rolagem -->
        <div class="ec-grafico__tabela-area" v-else>
            <table class="table table-sm table-hover ec-grafico__tabela">
                <caption class="sr-only">{{ titulo }}: {{ rotuloValorPlural }} por ano</caption>
                <thead class="ec-thead">
                    <tr>
                        <th scope="col" class="text-center">Ano</th>
                        <th scope="col" class="text-right ec-grafico__col-valor">{{ rotuloValorPlural }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="item in itens" :key="item.rotulo">
                        <td class="text-center" data-label="Ano">{{ item.rotulo }}</td>
                        <td class="text-right" :data-label="rotuloValorPlural">{{ formatar(item.valor) }}</td>
                    </tr>
                </tbody>
                <tfoot v-if="itens.length>1">
                    <tr>
                        <td class="text-center">Total</td>
                        <td class="text-right">{{ formatar(total) }}</td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </section>
</template>

<script>
import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, Tooltip, BarElement, CategoryScale, LinearScale } from 'chart.js'

ChartJS.register(Tooltip, BarElement, CategoryScale, LinearScale)

// tokens do tema (resources/sass/_variables.scss)
const COR_BARRA = '#1a7580'
const COR_BARRA_HOVER = '#1c4d5a'
const COR_TEXTO = '#1f2d33'
const COR_TEXTO_SUAVE = '#64767c'
const COR_GRADE = '#e3e9ed'
const FONTE = "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif"

const numero = new Intl.NumberFormat('pt-BR')

// rótulo direto apenas na barra mais recente (rótulos seletivos, não em todas)
const rotuloUltimaBarra = {
    id: 'rotuloUltimaBarra',
    afterDatasetsDraw(chart){
        const meta = chart.getDatasetMeta(0)
        const ultimo = meta.data.length - 1
        if(ultimo < 0){ return }
        const barra = meta.data[ultimo]
        const valor = chart.data.datasets[0].data[ultimo]
        const ctx = chart.ctx
        ctx.save()
        ctx.font = `700 12px ${FONTE}`
        ctx.fillStyle = COR_TEXTO
        ctx.textAlign = 'center'
        ctx.textBaseline = 'bottom'
        ctx.fillText(numero.format(valor), barra.x, barra.y - 6)
        ctx.restore()
    }
}

export default {
    components: { Bar },
    props:{
        titulo:{ type:String, required:true },
        subtitulo:{ type:String, default:'' },
        // [{ rotulo: '2024', valor: 12 }, ...]
        itens:{ type:Array, default:() => [] },
        rotuloValor:{ type:String, default:'registro' },
        rotuloValorPlural:{ type:String, default:'registros' },
        textoLink:{ type:String, default:'' },
        // total exibido no destaque; quando ausente usa a soma dos itens
        totalInformado:{ type:Number, default:null },
    },
    data(){
        return {
            exibirTabela:false,
            plugins:[ rotuloUltimaBarra ],
        }
    },
    computed:{
        somaItens(){
            return this.itens.reduce((soma, item) => soma + Number(item.valor || 0), 0)
        },
        total(){
            if(this.totalInformado !== null){
                return this.totalInformado
            }
            return this.somaItens
        },
        dados(){
            return {
                labels: this.itens.map(item => String(item.rotulo)),
                datasets: [{
                    label: this.rotuloValorPlural,
                    data: this.itens.map(item => Number(item.valor || 0)),
                    backgroundColor: COR_BARRA,
                    hoverBackgroundColor: COR_BARRA_HOVER,
                    borderRadius: { topLeft: 4, topRight: 4 },
                    borderSkipped: 'bottom',
                    maxBarThickness: 40,
                    categoryPercentage: 0.7,
                    barPercentage: 0.85,
                }]
            }
        },
        opcoes(){
            const singular = this.rotuloValor
            const plural = this.rotuloValorPlural
            return {
                responsive: true,
                maintainAspectRatio: false,
                animation: { duration: 400 },
                layout: { padding: { top: 22 } },
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        backgroundColor: '#0e2a31',
                        titleColor: '#ffffff',
                        bodyColor: '#d6e4e7',
                        titleFont: { family: FONTE, weight: '700', size: 12 },
                        bodyFont: { family: FONTE, size: 12 },
                        padding: 10,
                        cornerRadius: 8,
                        displayColors: false,
                        callbacks: {
                            title: itens => `Ano ${itens[0].label}`,
                            label: item => `${numero.format(item.raw)} ${item.raw == 1 ? singular : plural}`,
                        }
                    }
                },
                scales: {
                    x: {
                        grid: { display: false },
                        border: { color: COR_GRADE },
                        ticks: { color: COR_TEXTO_SUAVE, font: { family: FONTE, size: 11, weight: '600' } },
                    },
                    y: {
                        beginAtZero: true,
                        grace: '8%',
                        border: { display: false },
                        grid: { color: COR_GRADE, drawTicks: false },
                        ticks: {
                            color: COR_TEXTO_SUAVE,
                            font: { family: FONTE, size: 11 },
                            padding: 8,
                            precision: 0,
                            maxTicksLimit: 5,
                            callback: valor => numero.format(valor),
                        },
                    }
                }
            }
        }
    },
    methods:{
        formatar(valor){
            return numero.format(Number(valor || 0))
        }
    }
}
</script>

<style scoped>
    .ec-grafico{
        height: 100%;
        display: flex;
        flex-direction: column;
        padding: 1.1rem 1.25rem 1rem;
        text-align: left;
        background: #fff;
        border: 1px solid #e3e9ed;
        border-radius: .75rem;
        box-shadow: 0 1px 2px rgba(16, 42, 49, .06), 0 1px 3px rgba(16, 42, 49, .05);
    }

    .ec-grafico__cabecalho{
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 1rem;
        margin-bottom: .75rem;
    }

    .ec-grafico__titulos{
        display: flex;
        flex-direction: column;
        min-width: 0;
    }

    .ec-grafico__titulo{
        font-size: .72rem;
        font-weight: 700;
        letter-spacing: .06em;
        text-transform: uppercase;
        color: #64767c;
    }

    .ec-grafico__total{
        font-size: 1.9rem;
        font-weight: 800;
        line-height: 1.15;
        color: #1f2d33;
        font-variant-numeric: tabular-nums;
    }

    .ec-grafico__subtitulo{
        font-size: .75rem;
        color: #64767c;
    }

    .ec-grafico__acoes{
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: .4rem;
        flex-shrink: 0;
    }

    .ec-grafico__alternar{
        display: inline-flex;
        align-items: center;
        gap: .35rem;
        padding: .25rem .6rem;
        font-size: .72rem;
        font-weight: 600;
        color: #1c4d5a;
        background: #f3f6f8;
        border: 1px solid #e3e9ed;
        border-radius: 999px;
        cursor: pointer;
    }

    .ec-grafico__alternar:hover,
    .ec-grafico__alternar.ativo{
        border-color: #1a7580;
        background: rgba(26, 117, 128, .08);
    }

    .ec-grafico__link{
        font-size: .75rem;
        font-weight: 600;
        color: #1a7580;
        white-space: nowrap;
    }

    .ec-grafico__link i{
        font-size: .65rem;
        margin-left: .2rem;
        transition: transform .15s ease;
    }

    .ec-grafico__link:hover{
        color: #1c4d5a;
        text-decoration: none;
    }

    .ec-grafico__link:hover i{
        transform: translateX(2px);
    }

    .ec-grafico__area{
        position: relative;
        height: 220px;
    }

    .ec-grafico__tabela-area{
        height: 220px;
        overflow-y: auto;
        border: 1px solid #e3e9ed;
        border-radius: .5rem;
    }

    .ec-grafico__tabela{
        margin: 0;
    }

    .ec-grafico__tabela thead th{
        position: sticky;
        top: 0;
        z-index: 1;
        border-top: 0;
    }

    .ec-grafico__col-valor{
        text-transform: capitalize;
    }

    .ec-grafico__tabela tbody tr:nth-of-type(even):not(:hover) > td{
        background-color: rgba(227, 233, 237, .35);
    }

    /* total: destaque sutil, fixo no rodapé da rolagem */
    .ec-grafico__tabela tfoot td{
        position: sticky;
        bottom: 0;
        font-weight: 600;
        color: #1f2d33;
        background: #fafcfc;
        border-top: 2px solid #e3e9ed;
    }

    .ec-grafico__vazio{
        height: 220px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: .5rem;
        font-size: .85rem;
        color: #64767c;
        background: #f8fafb;
        border: 1px dashed #d5dee3;
        border-radius: .5rem;
    }

    .ec-grafico__vazio i{
        font-size: 1.5rem;
        opacity: .6;
    }
</style>
